import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { VngSelectOption } from "@vengreso/angular-components";

import { AuthService } from "src/app/core/core-services/services/auth.service";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { OnboardingProgressService } from "src/app/core/core-services/services/onboarding-progress.service";
import { SharedDataService } from "src/app/core/core-services/services/shared-data.service";

/**
 * The 6 feature-interest options confirmed with product 2026-08-14 (issue
 * #519, naming: "LinkedIn AI Comment Generator"), renamed on stakeholder
 * validation (issue #538): "Text Expander with Snippets" -> "Shortcuts (aka
 * FlyCuts)". This is BOTH the fallback list for an old backend that doesn't
 * return its own options at `question_index_id` 5 yet
 * (vengreso/flymsg-be#760) AND the fixed two-column DISPLAY ORDER — issue
 * #538 requires the grid to render in this exact order regardless of
 * whatever order the server's `options` array is in. Column 1 is the first
 * three entries, column 2 the last three (see `.fms-about-you-grid`'s
 * `grid-auto-flow: column`).
 */
export const FEATURE_INTEREST_OPTIONS: readonly string[] = [
  "Shortcuts (aka FlyCuts)",
  "AI Grammar Checker",
  "AI Paragraph Rewrite",
  "AI Sales Roleplay",
  "LinkedIn AI Comment Generator",
  "LinkedIn Social Post Generator",
];

/**
 * Rollout compatibility (issue #538): flymsg-be's companion rename
 * (flymsg-be#762) may not be merged yet when this ships, so
 * `GET v1/user/onboarding/questions` can still return the OLD string for
 * this option. Always DISPLAY the new label, but SUBMIT whatever value the
 * server sent verbatim — otherwise a POST carrying "Shortcuts (aka FlyCuts)"
 * against a backend that only recognizes "Text Expander with Snippets" would
 * be silently dropped by its seeded-options filter. Once both sides are on
 * `onboarding`, the server sends the new string and this is a no-op
 * passthrough.
 */
const OLD_TO_NEW_FEATURE_LABEL: Readonly<Record<string, string>> = {
  "Text Expander with Snippets": "Shortcuts (aka FlyCuts)",
};

function toDisplayLabel(serverOption: string): string {
  return OLD_TO_NEW_FEATURE_LABEL[serverOption] ?? serverOption;
}

/**
 * Per-user SharedDataService key for this browser's locally-persisted
 * feature-interest grid selection (issue #538 QA follow-up, findings
 * S-16/S-17/S-18 on the run that rejected PR #539). Mirrors
 * `OnboardingProgressService`'s `completedStepsStorageKey` exactly —
 * namespaced by user id so a shared browser switching accounts can't leak
 * one user's grid selection into another's.
 */
function featureInterestStorageKey(userId: string): string {
  return `about_you_feature_interest:${userId}`;
}

/**
 * QA finding S-03/S-04 on PR #532: the RAW `questions[]` array returned by
 * `GET v1/user/onboarding/questions` is NOT in question_index_id order — it's
 * seeder-authoring order (`OnboardingQuestionSeeder`: language, department,
 * "how do you plan to use FlyMSG" (radio), role, free-text use-case, feature
 * interest). Indexing it directly at raw positions 2/3/4 renders the radio
 * usage question, the role question and a zero-option free-text question —
 * never language, and never a real "department" question.
 *
 * The retired full-page wizard (`step1.component.ts`, still shipping)
 * resolves this the same way every time it renders/submits this document:
 * `questions.filter(q => !q.disabled).sort((a, b) =>
 * b.question_type.localeCompare(a.question_type))`. That sort groups by
 * question_type descending (text > select > radio > checkbox) and, because
 * none of these 6 questions are disabled, produces:
 *   [0] free-text use-case ("text")
 *   [1] language ("select")
 *   [2] department ("select")
 *   [3] role ("select")
 *   [4] "how do you plan to use FlyMSG" ("radio")
 *   [5] feature interest ("checkbox")
 * — which is EXACTLY the backend's question_index_id -> user_info map in
 * `UserController::storeOnboardingQuestionAnswers` (1 -> fly_grammar_default_
 * language, 2 -> department, 3 -> job_role, 4 -> flymsg_use_case [not shown
 * on this panel], 5 -> feature_interest). The sorted array's own 0-based
 * position IS the question_index_id to submit — no extra offset.
 */
const LANGUAGE_INDEX = 1;
const DEPARTMENT_INDEX = 2;
const ROLE_INDEX = 3;
export const FEATURE_INTEREST_INDEX = 5;

/** Same comparator as `step1.component.ts`'s `getTheQuestion()` — must stay byte-identical so the two surfaces keep agreeing on which question is which. */
function sortQuestions(questions: OnboardingQuestion[]): OnboardingQuestion[] {
  return questions.filter((q) => !q.disabled).sort((a, b) => b.question_type.localeCompare(a.question_type));
}

export interface OnboardingQuestion {
  question: string;
  question_type: string;
  options?: string[];
  answer?: string;
  disabled?: boolean;
}

export interface OnboardingQuestionDocument {
  id: string;
  questions: OnboardingQuestion[];
}

/**
 * Issue #519: the walk-me modal's required "About You" panel (step 1, right
 * after Install the Extension) — language/department/role selects plus the
 * feature-interest multi-select grid, extracted into its own component the
 * same way #523 pulled the header/checklist out of `welcome-modal.component`,
 * since this panel owns real fetch/submit/validation logic the others don't.
 *
 * Completion rule (decided with product): the checklist row ticks as soon as
 * language + department + role are all answered — the feature grid is shown
 * but never blocks it. `WelcomeModalComponent` still owns panel NAVIGATION
 * (the `continueClicked` output round-trips through its existing `next()`,
 * exactly like every other panel's "Try it out!" button), so this component
 * only owns the panel's OWN content and its own real completion signal.
 */
@Component({
  selector: "app-about-you-step",
  templateUrl: "./about-you-step.component.html",
  styleUrls: ["./about-you-step.component.scss"],
  standalone: false,
})
export class AboutYouStepComponent implements OnInit {
  /** WelcomeModalComponent's panel index for this step — feeds the legacyStep math on completeStep(), same convention as every other panel. */
  @Input() step = 1;
  /** WelcomeModalComponent's current maxStep — completeStep()'s legacyMaxStepExclusive is always maxStep + 1. */
  @Input() maxStep = 0;
  @Output() readonly continueClicked = new EventEmitter<void>();

  languageQuestion: OnboardingQuestion | null = null;
  departmentQuestion: OnboardingQuestion | null = null;
  roleQuestion: OnboardingQuestion | null = null;
  featureOptions: readonly string[] = FEATURE_INTEREST_OPTIONS;
  readonly selectedFeatures = new Set<string>();
  loadFailed = false;
  /**
   * Issue #538: true until `GET v1/user/onboarding/questions` resolves
   * (success OR error). While true, the template renders `vng-skeleton-rows`
   * and none of the form — the grid used to render immediately off the local
   * fallback constant while the three selects were still waiting on the
   * fetch, a partial-content flash stakeholders flagged. Everything now
   * appears together, in one paint, once this flips false.
   */
  isLoading = true;

  private questionsDoc: OnboardingQuestionDocument | null = null;
  /** Display label (the FE's, always the NEW name) -> the exact value to submit (the server's own string — see `toDisplayLabel`). */
  private featureSubmitValueByLabel = new Map<string, string>();

  constructor(
    private readonly dataService: DataService,
    private readonly authService: AuthService,
    public readonly onboardingProgress: OnboardingProgressService,
    private readonly sharedData: SharedDataService
  ) {}

  private get userId(): string {
    return this.authService.userDetails?.user_details?.id ?? "";
  }

  ngOnInit(): void {
    this.dataService.get({ url: "v1/user/onboarding/questions", isLoader: false }).subscribe({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      next: (response: any) => {
        this.applyQuestions(response?.result?.data?.[0] ?? null);
        this.isLoading = false;
      },
      error: () => {
        this.loadFailed = true;
        this.isLoading = false;
      },
    });
  }

  get requiredAnswered(): boolean {
    return !!this.languageQuestion?.answer && !!this.departmentQuestion?.answer && !!this.roleQuestion?.answer;
  }

  getSelectOptions(question: OnboardingQuestion | null): VngSelectOption[] {
    return (question?.options ?? []).filter((option) => option !== "Other").map((option) => ({ value: option, label: option }));
  }

  /** Slugifies a feature label for a stable, space-free Heap event id (e.g. "AI Grammar Checker" -> "ai-grammar-checker"). */
  featureHeapId(option: string): string {
    return `walk-me-modal-about-you-feature-${option.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;
  }

  onLanguageChange(): void {
    this.onRequiredAnswerChange(this.languageQuestion, LANGUAGE_INDEX);
  }

  onDepartmentChange(): void {
    this.onRequiredAnswerChange(this.departmentQuestion, DEPARTMENT_INDEX);
  }

  onRoleChange(): void {
    this.onRequiredAnswerChange(this.roleQuestion, ROLE_INDEX);
  }

  /** The template iterates DISPLAY labels (`featureOptions`); checked-state is tracked by SUBMIT value, so the old/new rename never desyncs the two. */
  isFeatureChecked(displayLabel: string): boolean {
    return this.selectedFeatures.has(this.submitValueFor(displayLabel));
  }

  onFeatureToggle(displayLabel: string, checked: boolean): void {
    const value = this.submitValueFor(displayLabel);
    if (checked) {
      this.selectedFeatures.add(value);
    } else {
      this.selectedFeatures.delete(value);
    }
    this.submitAnswer(FEATURE_INTEREST_INDEX, JSON.stringify(Array.from(this.selectedFeatures)));
    // Issue #538 QA follow-up: every user-driven change also updates this
    // browser's local snapshot, so the NEXT mount (modal reopen, reload) seeds
    // from it — see `seedFeatureInterest()` — instead of falling through to
    // the opt-out defaults and silently re-checking/re-submitting what the
    // user just deselected.
    this.persistFeatureInterestLocally(this.userId);
  }

  onContinue(): void {
    this.continueClicked.emit();
  }

  private applyQuestions(doc: OnboardingQuestionDocument | null): void {
    if (!doc) {
      this.loadFailed = true;
      return;
    }
    this.questionsDoc = doc;
    // QA S-03/S-04 on PR #532: must index the FILTERED+SORTED array (see
    // sortQuestions() above), never the raw doc.questions order.
    const questions = sortQuestions(doc.questions ?? []);
    this.languageQuestion = questions[LANGUAGE_INDEX] ?? null;
    this.departmentQuestion = questions[DEPARTMENT_INDEX] ?? null;
    this.roleQuestion = questions[ROLE_INDEX] ?? null;

    const featureQuestion = questions[FEATURE_INTEREST_INDEX];
    const serverOptions = featureQuestion?.options?.length ? featureQuestion.options : FEATURE_INTEREST_OPTIONS;
    // Issue #538: SUBMIT the server's own string verbatim (old or new,
    // whichever it sent) but always DISPLAY/order via the FE's fixed
    // constant — never the server's option order.
    this.featureSubmitValueByLabel = new Map(serverOptions.map((raw) => [toDisplayLabel(raw), raw]));
    this.featureOptions = FEATURE_INTEREST_OPTIONS;

    this.submitPreAnsweredRequired();
    this.seedFeatureInterest(featureQuestion);
  }

  /** Display label -> the exact string to submit; falls back to the label itself if the server's option list didn't include a match. */
  private submitValueFor(displayLabel: string): string {
    return this.featureSubmitValueByLabel.get(displayLabel) ?? displayLabel;
  }

  /**
   * Issue #538 QA follow-up (run `2026-08-17-about-you-step-loading-538`,
   * findings S-16/S-17/S-18 that rejected PR #539): the original
   * `initializeFeatureInterestDefaults()` applied the opt-out "all six
   * checked" default UNCONDITIONALLY on every `applyQuestions()` call — and
   * the panel remounts (a fresh `applyQuestions()`) every time the onboarding
   * modal is opened, reloaded, or the user signs back in. That silently
   * re-checked and re-submitted a returning user's deliberate deselections,
   * overwriting `user_info.feature_interest` server-side with the full set
   * even when the user never touched the grid that visit.
   *
   * Seeding priority, most to least authoritative — and NOTE only branch (c)
   * ever submits:
   *  (a) The server's own per-user answer (flymsg-be#764: `question_index_id`
   *      5's `answer` is a `;`-joined subset of option strings, in
   *      POST-RENAME/display naming). When present, this IS the user's
   *      current stored state — seed from it, no submit.
   *  (b) This browser's local snapshot (SharedDataService, mirroring
   *      `OnboardingProgressService`'s completed-steps pattern — see
   *      `featureInterestStorageKey`). Written on every real change (see
   *      `onFeatureToggle`) and by branch (c) below, so once a user has ever
   *      had a grid state on this browser it stays authoritative here in the
   *      absence of (a) — seed from it, no submit.
   *  (c) Genuinely first-time — neither (a) nor (b) exists: the opt-out
   *      default (all six checked), submitted ONCE, and immediately persisted
   *      locally so every subsequent mount for this user (even before
   *      flymsg-be#764 ships) lands in (b) instead of repeating (c).
   * Every branch ends with the resolved state persisted locally, so the
   * cache never goes stale relative to whichever source just won — this is
   * what makes the initial POST fire once per USER, not once per mount.
   */
  private seedFeatureInterest(featureQuestion: OnboardingQuestion | undefined): void {
    const userId = this.userId;

    const fromServer = this.parseServerFeatureAnswer(featureQuestion?.answer);
    if (fromServer) {
      this.replaceSelectedFeatures(fromServer);
      this.persistFeatureInterestLocally(userId);
      return;
    }

    const fromLocal = userId ? this.readLocalFeatureInterest(userId) : null;
    if (fromLocal) {
      this.replaceSelectedFeatures(new Set(fromLocal));
      // Already exactly what's stored under this key — nothing new to persist.
      return;
    }

    // (c) genuinely first-time: opt-out default, submitted once, persisted.
    for (const label of this.featureOptions) {
      this.selectedFeatures.add(this.submitValueFor(label));
    }
    this.submitAnswer(FEATURE_INTEREST_INDEX, JSON.stringify(Array.from(this.selectedFeatures)));
    this.persistFeatureInterestLocally(userId);
  }

  /** Parses flymsg-be#764's `;`-joined per-user answer (display-label naming) into the SUBMIT values it represents, using this load's label->submit-value map. Null (not an empty set) when absent, so callers can fall through to the next seeding source — an intentionally-empty stored selection would instead arrive as `answer: ""`, which also parses to null here and correctly falls through, since flymsg-be#764 is not expected to distinguish "answered with nothing" from "never answered" on this field. */
  private parseServerFeatureAnswer(answer: string | undefined): Set<string> | null {
    const labels = (answer ?? "")
      .split(";")
      .map((label) => label.trim())
      .filter(Boolean);
    if (!labels.length) {
      return null;
    }
    return new Set(labels.map((label) => this.submitValueFor(label)));
  }

  /** Reads this browser's local feature-interest snapshot for a user. Null (not []) when nothing was ever persisted, so callers can distinguish "no snapshot yet" from "snapshot says nothing selected". */
  private readLocalFeatureInterest(userId: string): string[] | null {
    const stored = this.sharedData.getAttribute<unknown>(featureInterestStorageKey(userId));
    if (!Array.isArray(stored)) {
      return null;
    }
    return stored.filter((value): value is string => typeof value === "string");
  }

  /** Writes this browser's local feature-interest snapshot. No-ops for an unknown user id (nothing to key it under). */
  private persistFeatureInterestLocally(userId: string): void {
    if (!userId) {
      return;
    }
    this.sharedData.setAttribute(featureInterestStorageKey(userId), Array.from(this.selectedFeatures));
  }

  /** Resets `selectedFeatures` (readonly Set instance, kept stable for template bindings) to exactly the given submit values. */
  private replaceSelectedFeatures(values: ReadonlySet<string>): void {
    this.selectedFeatures.clear();
    for (const value of values) {
      this.selectedFeatures.add(value);
    }
  }

  /**
   * QA S-07 on PR #532: the question document can arrive with a required
   * question already answered — the language question ships seeded with a
   * default (e.g. "American English"). `requiredAnswered` only checks that
   * `.answer` is present, so a user who never touches that dropdown would
   * still complete the step without that default ever being POSTed,
   * silently leaving `fly_grammar_default_language` unset server-side. The
   * retired full-page wizard's Next button always POSTs every question,
   * defaults included — this replicates that, once, right after the fetch
   * resolves: idempotent because `applyQuestions()` itself only ever runs
   * once per fetch, so this never fires twice for the same load; it never
   * fires for a question with no pre-existing answer; and it never
   * conflicts with a LATER user-driven change, which POSTs again through
   * `onLanguageChange()`/etc. exactly as before — that second POST carries
   * the user's actual choice, not a duplicate of the default.
   *
   * flymsg-be#764 note: once the backend returns each question's own
   * per-user stored value at `.answer` (rather than only language's global
   * signup-time default), the three selects above already re-hydrate from it
   * for free — `applyQuestions()` assigns the question objects straight off
   * the response, and the template's `[(ngModel)]="…Question.answer"`
   * binding shows whatever is there on mount, no extra code needed. This
   * submit-once guard intentionally keeps firing whenever `.answer` is
   * present either way: unlike the feature-interest grid (issue #538 QA
   * S-16/S-17/S-18), a select has exactly one current value, so resubmitting
   * an already-stored answer is a harmless no-op, never a silent overwrite of
   * something the user chose differently — there is no "many selections
   * collapsed into one union" step here for a stale resubmit to corrupt.
   */
  private submitPreAnsweredRequired(): void {
    const required: Array<[OnboardingQuestion | null, number]> = [
      [this.languageQuestion, LANGUAGE_INDEX],
      [this.departmentQuestion, DEPARTMENT_INDEX],
      [this.roleQuestion, ROLE_INDEX],
    ];
    for (const [question, index] of required) {
      if (question?.answer) {
        this.submitAnswer(index, question.answer);
      }
    }
    this.maybeComplete();
  }

  private onRequiredAnswerChange(question: OnboardingQuestion | null, index: number): void {
    if (!question) {
      return;
    }
    this.submitAnswer(index, question.answer ?? "");
    this.maybeComplete();
  }

  private maybeComplete(): void {
    if (!this.requiredAnswered || this.onboardingProgress.isCompleted("about_you")) {
      return;
    }
    this.onboardingProgress.completeStep("about_you", this.step + 1, this.maxStep + 1).subscribe();
  }

  private submitAnswer(questionIndexId: number, answer: string): void {
    if (!this.questionsDoc) {
      return;
    }
    const userId = this.userId;
    this.dataService
      .post({
        url: "v1/user/onboarding/answer",
        // QA G-06 on PR #532: without this, every answer POST (including the
        // pre-answered-default auto-submit added for S-07) raised the app's
        // global ngx-ui-loader full-screen overlay for ~1.4s, unprompted,
        // swallowing clicks underneath it. Matches the GET above (isLoader:
        // false) — this panel manages its own inline UI, it never needs the
        // global blocking spinner for a background answer submit.
        isLoader: false,
        data: {
          step: "1",
          question_id: this.questionsDoc.id,
          question_index_id: questionIndexId,
          answer,
          user_id: userId,
        },
      })
      .subscribe();
  }
}
